import { useRef, useSyncExternalStore, type SetStateAction } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import { draftContextKey, isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';

type Slot = { owner: DraftContext; value: unknown; listeners: Set<() => void> };
const slots = new Map<string, Slot>();
function removeDepartedWork() {
  for (const [key, slot] of slots) if (!isCurrentDraftContext(slot.owner)) {
    slots.delete(key);
    slot.listeners.forEach(listener => listener());
  }
}
useAuthStore.subscribe(removeDepartedWork);
useScopeStore.subscribe(removeDepartedWork);

export function clearOwnedAssistState(owner: DraftContext, name: string): void {
  const prefix = `${draftContextKey(owner)}:${name}`;
  for (const key of slots.keys()) if (key === prefix || key.startsWith(`${prefix}:`)) slots.delete(key);
}

/** Transient same-tab work survives source/goal navigation, never an owner change.
 * No raw capture or provider payload is copied into persistent browser storage. */
export function useOwnedAssistState<T>(owner: DraftContext, name: string, initial: T | (() => T)): [T, (next: SetStateAction<T>) => void] {
  const key = `${draftContextKey(owner)}:${name}`;
  const ref = useRef<Slot>();
  if (!ref.current) {
    const value = initial instanceof Function ? initial() : initial;
    ref.current = slots.get(key) ?? { owner, value, listeners: new Set() };
    if (isCurrentDraftContext(owner)) slots.set(key, ref.current);
  }
  const slot = ref.current;
  const value = useSyncExternalStore(listener => {
    slot.listeners.add(listener);
    return () => { slot.listeners.delete(listener); };
  }, () => slot.value as T, () => slot.value as T);
  return [value, next => {
    if (!isCurrentDraftContext(owner) || slots.get(key) !== slot) return;
    slot.value = next instanceof Function ? next(slot.value as T) : next;
    slot.listeners.forEach(listener => listener());
  }];
}
